import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Banknote,
  ClipboardList,
  Command,
  FileText,
  Layers,
  Percent,
} from 'lucide-react';

import { useLeadList } from '@/features/leads/api';
import { PageContainer } from '@/shared/ui';
import { Button } from '@/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';

import {
  useAdminDashboard,
  useLeadPipeline,
  useOperationsPulse,
  useOperationsWeek,
  useQuotePipeline,
} from '../api';
import {
  AdminPendingQuotesTable,
  AdminQuickActions,
  AdminRecentLeads,
  AdminUpcomingMeetings,
  AdminWorkOrdersTable,
  CompletionTrend,
  DashboardError,
  DashboardPageHeader,
  DashboardSkeleton,
  DonutChart,
  KpiCard,
  LeadFunnel,
  OpsPulseStrip,
  type DonutDatum,
} from '../components';

/** Compact NZD for KPI tiles (e.g. "$1.2M", "$45K"). */
function formatCompactCurrency(amount: number): string {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount || 0);
}

/** Build KpiCard delta props from a signed number (omitted when unavailable). */
function deltaProps(
  value: number | undefined,
  unit: 'pct' | 'pts',
): { delta?: string; isIncrease?: boolean } {
  if (value === undefined || !Number.isFinite(value)) return {};
  const txt =
    unit === 'pts'
      ? `${Math.abs(value).toFixed(1)} pts`
      : `${Math.abs(value).toFixed(1)}%`;
  return { delta: txt, isIncrease: value >= 0 };
}

function SectionLabel({
  title,
  badge,
  action,
}: {
  title: string;
  badge?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="tw-mb-3 tw-mt-7 tw-flex tw-items-center tw-gap-2.5 first:tw-mt-0">
      <h2 className="tw-m-0 tw-text-[11.5px] tw-font-bold tw-uppercase tw-tracking-[0.09em] tw-text-muted-foreground">
        {title}
      </h2>
      {badge ? (
        <span className="tw-rounded-full tw-bg-primary/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-primary">
          {badge}
        </span>
      ) : null}
      <span className="tw-h-px tw-flex-1 tw-bg-gradient-to-r tw-from-border tw-to-transparent" />
      {action}
    </div>
  );
}

/**
 * Route: /admin-dashboard — Scaffold Operations Command Center.
 *
 * Phase 1 enterprise rebuild: a live operational pulse (crews / activities /
 * tasks / assets / backlog / at-risk), pipeline graphs (lead funnel, quote
 * conversion, activities-by-status, planned-vs-completed) and delivery + quick
 * data. All FE-derived — reuses the dispatch-board insights aggregator, the
 * quote/lead lists, and the existing /Dashboard/Admin payload. No redeploy.
 */
export default function AdminModernDashboardPage() {
  React.useEffect(() => {
    document.title = 'Scaffold Operations Command Center | Euro Scaffolds';
  }, []);

  const { data, isLoading, isError, error, refetch } = useAdminDashboard(5);

  return (
    <PageContainer maxWidth="1800">
      <div className="tw-flex tw-flex-col tw-gap-2">
        <DashboardPageHeader
          icon={Command}
          title="Scaffold Operations Command Center"
          description="Live operational health across crews, scaffolds, work orders, leads & quotes."
        />

        {isLoading ? <DashboardSkeleton /> : null}

        {isError && !data ? (
          <DashboardError
            message={error instanceof Error ? error.message : undefined}
            onRetry={() => refetch()}
          />
        ) : null}

        {data ? (
          <AdminDashboardBody
            data={data}
            errorBanner={
              isError ? (error instanceof Error ? error.message : 'Unknown error') : null
            }
          />
        ) : null}
      </div>
    </PageContainer>
  );
}

interface AdminDashboardBodyProps {
  data: NonNullable<ReturnType<typeof useAdminDashboard>['data']>;
  errorBanner: string | null;
}

function AdminDashboardBody({ data, errorBanner }: AdminDashboardBodyProps) {
  const adminKpis = data.kpIs ??
    data.kpis ?? {
      activeScaffolds: 0,
      activeWorkOrders: 0,
      quotesPending: 0,
      totalQuotes: 0,
      inventoryItems: 0,
      lowStockItems: 0,
    };

  const scaffoldStatusBreakdown = data.scaffoldStatusBreakdown ?? {
    planned: 0,
    inProgress: 0,
    draft: 0,
  };
  const recentWorkOrders = data.recentWorkOrders ?? [];
  const pendingQuotes = data.pendingQuotes ?? [];
  const upcomingMeetings = data.upcomingMeetings ?? [];

  // Live operational + pipeline data (all FE-derived).
  const pulse = useOperationsPulse();
  const leadPipeline = useLeadPipeline();
  const quotePipeline = useQuotePipeline();
  const leadsQ = useLeadList({ pageSize: 200 });
  const recentLeads = (leadsQ.data?.items ?? []).filter((l) => !l.isDeleted).slice(0, 5);

  const insights = useOperationsWeek();

  // --- chart datasets -------------------------------------------------------
  const quoteOutcomeDonut: DonutDatum[] = quotePipeline.outcomes.map((o) => ({
    label: o.label,
    value: o.count,
    color: o.color,
    secondary: formatCompactCurrency(o.value),
  }));

  const activityStatusDonut: DonutDatum[] = (insights?.statusBreakdown ?? [])
    .filter((s) => s.value > 0)
    .map((s) => ({ label: s.label, value: s.value, color: s.fill }));
  const activitiesTotal = insights?.kpis.totalActivities ?? 0;
  const completionRate =
    activitiesTotal > 0
      ? ((insights?.kpis.completedActivities ?? 0) / activitiesTotal) * 100
      : 0;

  const scaffoldDonut: DonutDatum[] = [
    { label: 'In progress', value: scaffoldStatusBreakdown.inProgress, color: '#f7b84b' },
    { label: 'Planned', value: scaffoldStatusBreakdown.planned, color: '#405189' },
    { label: 'Draft', value: scaffoldStatusBreakdown.draft, color: '#94a3b8' },
  ];
  const scaffoldTotal =
    scaffoldStatusBreakdown.inProgress +
    scaffoldStatusBreakdown.planned +
    scaffoldStatusBreakdown.draft;

  return (
    <div className="tw-flex tw-flex-col">
      {errorBanner ? (
        <Card className="tw-mb-4 tw-border-warning/50">
          <CardContent className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-p-4">
            <p className="tw-m-0 tw-text-sm tw-text-warning-foreground">
              Showing cached data — last refresh failed: {errorBanner}
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* ===================== TODAY · OPERATIONAL PULSE ===================== */}
      <SectionLabel
        title="Today · Operational Pulse"
        action={
          <Button asChild variant="ghost" size="sm">
            <Link to="/planner">Open Dispatch Board</Link>
          </Button>
        }
      />
      <OpsPulseStrip pulse={pulse} />

      {/* ===================== BUSINESS HEALTH (KPIs) ===================== */}
      <SectionLabel title="Business Health" />
      <div className="tw-grid tw-grid-cols-2 tw-gap-3.5 md:tw-grid-cols-3 xl:tw-grid-cols-5">
        <KpiCard
          label="Open pipeline value"
          value={formatCompactCurrency(quotePipeline.pipelineValue)}
          sub="quotes still in play"
          icon={Banknote}
          accent="primary"
          index={0}
          {...deltaProps(quotePipeline.pipelineValueDeltaPct, 'pct')}
        />
        <KpiCard
          label="Quote win rate"
          value={`${Math.round(quotePipeline.winRate)}%`}
          sub="converted ÷ all quotes"
          icon={Percent}
          accent="success"
          index={1}
          {...deltaProps(quotePipeline.winRateDeltaPts, 'pts')}
        />
        <KpiCard
          label="Active scaffolds"
          value={String(adminKpis.activeScaffolds)}
          sub={`${scaffoldStatusBreakdown.inProgress} in progress · ${scaffoldStatusBreakdown.planned} planned`}
          icon={Layers}
          accent="warning"
          index={2}
        />
        <KpiCard
          label="Active work orders"
          value={String(adminKpis.activeWorkOrders)}
          sub="in progress"
          icon={ClipboardList}
          accent="secondary"
          index={3}
        />
        <KpiCard
          label="Quotes pending"
          value={String(adminKpis.quotesPending)}
          sub="awaiting approval"
          icon={FileText}
          accent="primary"
          index={4}
        />
      </div>

      {/* ===================== PIPELINE · LEADS & QUOTES ===================== */}
      <SectionLabel
        title="Pipeline · Leads & Quotes"
        action={
          <Button asChild variant="ghost" size="sm">
            <Link to="/quotes/report">Conversion report</Link>
          </Button>
        }
      />
      <div className="tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-2">
        {/* Lead pipeline funnel */}
        <Card>
          <CardHeader className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-space-y-0">
            <div>
              <CardTitle className="tw-text-base">Lead pipeline</CardTitle>
              <CardDescription className="tw-mt-0.5">
                Distribution by stage.
              </CardDescription>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/leads/list">Leads</Link>
            </Button>
          </CardHeader>
          <CardContent>
            <LeadFunnel pipeline={leadPipeline} />
          </CardContent>
        </Card>

        {/* Quote conversion */}
        <Card>
          <CardHeader className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-space-y-0">
            <div>
              <CardTitle className="tw-text-base">Quote conversion</CardTitle>
              <CardDescription className="tw-mt-0.5">
                Every quote in exactly one outcome.
              </CardDescription>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/quotes/list">Quotes</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {quotePipeline.total === 0 ? (
              <p className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
                No quotes yet.
              </p>
            ) : (
              <>
                <DonutChart
                  data={quoteOutcomeDonut}
                  centerValue={String(quotePipeline.total)}
                  centerLabel="QUOTES"
                  showPct
                />
                <p className="tw-mt-3 tw-border-t tw-border-border tw-pt-3 tw-text-[11.5px] tw-text-muted-foreground">
                  Total quoted value{' '}
                  <b className="tw-text-foreground tw-tabular-nums">
                    {formatCompactCurrency(quotePipeline.totalValue)}
                  </b>
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Activities by status (insights aggregator) */}
        <Card>
          <CardHeader className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-space-y-0">
            <div>
              <CardTitle className="tw-text-base">Activities by status</CardTitle>
              <CardDescription className="tw-mt-0.5">
                This week · from the dispatch board.
              </CardDescription>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/planner">Board</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {activitiesTotal === 0 ? (
              <p className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
                No activities scheduled this week.
              </p>
            ) : (
              <DonutChart
                data={activityStatusDonut}
                centerValue={String(activitiesTotal)}
                centerLabel="ACTIVITIES"
              />
            )}
          </CardContent>
        </Card>

        {/* Planned vs completed (insights aggregator) */}
        <Card>
          <CardHeader className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-space-y-0">
            <div>
              <CardTitle className="tw-text-base">Planned vs completed</CardTitle>
              <CardDescription className="tw-mt-0.5">
                Daily delivery · this week.
              </CardDescription>
            </div>
            <Activity className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <CompletionTrend
              points={insights?.completion ?? []}
              completionRate={completionRate}
              avgPerDay={insights?.kpis.averageActivitiesPerDay}
            />
          </CardContent>
        </Card>
      </div>

      {/* ===================== DELIVERY + QUICK DATA ===================== */}
      <SectionLabel title="Delivery in Flight & Quick Data" />
      <div className="tw-grid tw-grid-cols-1 tw-gap-3 lg:tw-grid-cols-3">
        <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-col-span-2">
          <Card>
            <CardHeader className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-space-y-0">
              <div>
                <CardTitle className="tw-text-base">Active scaffolds by status</CardTitle>
                <CardDescription className="tw-mt-0.5">
                  Snapshot of in-flight deployments.
                </CardDescription>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/scaffolds/list">Scaffolds</Link>
              </Button>
            </CardHeader>
            <CardContent>
              {scaffoldTotal === 0 ? (
                <p className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
                  No active scaffolds.
                </p>
              ) : (
                <DonutChart
                  data={scaffoldDonut}
                  centerValue={String(scaffoldTotal)}
                  centerLabel="SCAFFOLDS"
                  showPct
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-space-y-0">
              <div>
                <CardTitle className="tw-text-base">Work orders in flight</CardTitle>
                <CardDescription className="tw-mt-0.5">
                  Most recent active jobs.
                </CardDescription>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/clients/work-orders">View all</Link>
              </Button>
            </CardHeader>
            <CardContent>
              <AdminWorkOrdersTable workOrders={recentWorkOrders} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-space-y-0">
              <div>
                <CardTitle className="tw-text-base">Quotes pending</CardTitle>
                <CardDescription className="tw-mt-0.5">
                  Need attention to advance the pipeline.
                </CardDescription>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/quotes/list">View all</Link>
              </Button>
            </CardHeader>
            <CardContent>
              <AdminPendingQuotesTable quotes={pendingQuotes} />
            </CardContent>
          </Card>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-3">
          <Card>
            <CardHeader className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-space-y-0 tw-p-4 tw-pb-2">
              <CardTitle className="tw-text-base">Recent leads</CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/leads/list">View all</Link>
              </Button>
            </CardHeader>
            <CardContent className="tw-p-4 tw-pt-0">
              <AdminRecentLeads leads={recentLeads} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-space-y-0 tw-p-4 tw-pb-2">
              <CardTitle className="tw-text-base">Upcoming meetings</CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/meetings/calendar">View all</Link>
              </Button>
            </CardHeader>
            <CardContent className="tw-p-4 tw-pt-0">
              <AdminUpcomingMeetings meetings={upcomingMeetings} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ===================== QUICK ACTIONS ===================== */}
      <SectionLabel title="Quick Actions" />
      <Card>
        <CardContent className="tw-pt-5">
          <AdminQuickActions />
        </CardContent>
      </Card>
    </div>
  );
}
